import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { phraseKey } from '@app/shared/place-rules';
import { api, ApiError, fieldIssues } from '../api';
import { can, type Me } from '../auth/me';
import { PlaceField } from '../events/PlaceField';
import { placeOfDraft } from '../events/autoPlace';
import type { Place } from '../events/place';
import { RowMenu } from '../ui/RowMenu';
import { Notice } from '../ui/Notice';
import { leaveGuarded, sameValues, useOpening, useUnsavedGuard } from '../ui/unsaved';
import { COMPANY_KEY, SETTINGS_RESOURCE, useCompanySettings, type CompanySettings } from './company';
import { EMPTY_PLACE, fullPlace, PLACE_RULES_KEY, placeLabel, usePlaceRules, type PlaceRule, type PlaceValue } from './placeDefaults';

const isEmpty = (p: PlaceValue) => p.location.trim() === '' && p.locationAddress.trim() === '' && p.locationPoint === null;
/** A place to send: trimmed, the event's shape. */
const toSend = (p: Place): PlaceValue => {
  const v = placeOfDraft(p);
  return { ...v, location: v.location.trim(), locationAddress: v.locationAddress.trim() };
};

function Problem({ error, conflict }: { error: unknown; conflict: string }) {
  if (!(error instanceof ApiError)) return null;
  return (
    <p className="alert" role="alert">
      {error.status === 409 ? conflict : fieldIssues(error).map((i) => i.message).join(' ') || error.message}
    </p>
  );
}

/** „Domyślne miejsce nowego wydarzenia” (owner 2026-10-30 22:20): one for the company; empty = none. */
function DefaultPlaceForm({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const current = useCompanySettings().data;
  const [place, setPlace] = useState<Place>(EMPTY_PLACE);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (current) setPlace(fullPlace(current.defaultPlace));
  }, [current]);
  const save = useMutation({
    mutationFn: () => api<CompanySettings>('/api/events/settings', { method: 'PATCH', body: { version: current!.version, defaultPlace: isEmpty(placeOfDraft(place)) ? null : toSend(place) } }),
    onSuccess: ({ moved: _moved, ...next }) => {
      queryClient.setQueryData(COMPANY_KEY, next);
      setSaved(true);
    },
  });
  const guardRef = useUnsavedGuard({
    dirty: canEdit && !!current && !sameValues(placeOfDraft(place), fullPlace(current.defaultPlace)),
    save: async () => {
      if (!current) return false;
      await save.mutateAsync();
      return true;
    },
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    e.stopPropagation();
    setSaved(false);
    save.mutate();
  }
  const change = (patch: Partial<Place>) => {
    setPlace((p) => ({ ...p, ...patch }));
    setSaved(false);
  };
  return (
    <form ref={guardRef} className="settings-form plain" aria-label="Domyślne miejsce nowego wydarzenia" onSubmit={submit}>
      <h3>Domyślne miejsce nowego wydarzenia</h3>
      <p className="hint">Nowe wydarzenie przychodzi z tym miejscem — zawsze można je zmienić. Puste: bez domyślnego miejsca.</p>
      <fieldset disabled={!canEdit || !current}>
        <PlaceField value={place} onChange={change} name="" bare namePlaceholder="np. Gdańsk" />
        <Problem error={save.error} conflict="Ktoś właśnie zmienił ustawienia. Zamknij i otwórz je jeszcze raz." />
        {saved && <Notice onClose={() => setSaved(false)}>Zapisano domyślne miejsce.</Notice>}
        {canEdit && (
          <div className="actions">
            <button className="btn primary" type="submit" disabled={save.isPending}>
              {save.isPending ? 'Zapisywanie…' : 'Zapisz domyślne miejsce'}
            </button>
            <button className="btn" type="button" disabled={isEmpty(placeOfDraft(place))} onClick={() => change(EMPTY_PLACE)}>
              Bez domyślnego miejsca
            </button>
          </div>
        )}
      </fieldset>
    </form>
  );
}

/** Add or change a rule: the phrase of the name and the place (the event's place field). */
function RuleForm({ base, onDone }: { base: PlaceRule | null; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [phrase, setPhrase] = useState(base?.phrase ?? '');
  const [place, setPlace] = useState<Place>(() => fullPlace(base?.place));
  const ready = phraseKey(phrase) !== '' && !isEmpty(placeOfDraft(place));
  const save = useMutation({
    mutationFn: () => {
      const body = { phrase: phrase.trim(), place: toSend(place) };
      return base ? api(`/api/events/place-rules/${base.id}`, { method: 'PATCH', body: { version: base.version, ...body } }) : api('/api/events/place-rules', { method: 'POST', body });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PLACE_RULES_KEY });
      onDone();
    },
  });
  const opening = useOpening({ phrase, place: placeOfDraft(place) });
  const guardRef = useUnsavedGuard({
    dirty: !sameValues({ phrase, place: placeOfDraft(place) }, opening),
    save: async () => {
      if (!ready) return false;
      await save.mutateAsync();
      return true;
    },
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (ready && !save.isPending) save.mutate();
  }
  return (
    <form ref={guardRef} className="settings-form" aria-label={base ? `Skojarzenie: ${base.phrase}` : 'Nowe skojarzenie'} onSubmit={submit}>
      <label className="field">
        <span>Fraza w nazwie wydarzenia</span>
        <input autoFocus value={phrase} maxLength={100} onChange={(e) => setPhrase(e.target.value)} autoComplete="off" placeholder="np. PFK" />
      </label>
      <PlaceField value={place} onChange={(patch) => setPlace((p) => ({ ...p, ...patch }))} name="" bare namePlaceholder="np. Sopot, Mamuszki 14" />
      <Problem error={save.error} conflict="Ktoś już zmienił to skojarzenie. Zamknij i otwórz listę jeszcze raz." />
      <div className="actions">
        <button className="btn primary" type="submit" disabled={save.isPending || !ready}>
          {save.isPending ? 'Zapisywanie…' : 'Zapisz skojarzenie'}
        </button>
        <button className="btn" type="button" onClick={onDone}>
          Anuluj
        </button>
      </div>
    </form>
  );
}

/**
 * „Miejsce nowego wydarzenia” in "Ustawienia firmy" (owner 2026-10-30 22:20 / 22:23): the default place and
 * „Skojarzenia miejsc” — a phrase in the name of a new event („PFK”) gives its place („Sopot, Mamuszki 14”). Everyone
 * who sees events reads them; changing them is "Ustawienia firmy" (edit), as the warehouse.
 */
export function NewEventPlaceSettings({ me, heading = true }: { me: Me; heading?: boolean }) {
  const queryClient = useQueryClient();
  const canEdit = can(me, SETTINGS_RESOURCE, 'edit');
  const list = usePlaceRules();
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [confirm, setConfirm] = useState<PlaceRule | null>(null);
  const remove = useMutation({
    mutationFn: (r: PlaceRule) => api(`/api/events/place-rules/${r.id}?version=${r.version}`, { method: 'DELETE' }),
    onSuccess: async () => {
      setConfirm(null);
      await queryClient.invalidateQueries({ queryKey: PLACE_RULES_KEY });
    },
  });
  const rules = list.data?.rules ?? [];
  return (
    <section className="settings-section" aria-label="Miejsce nowego wydarzenia" data-feature-id="events.place_defaults">
      {heading && <h2>Miejsce nowego wydarzenia</h2>}
      <DefaultPlaceForm canEdit={canEdit} />
      <h3>Skojarzenia miejsc</h3>
      <p className="hint">Gdy nazwa nowego wydarzenia zawiera frazę (np. „PFK Gala”), miejsce wpisuje się samo. Wygrywa z domyślnym miejscem; przy kilku pasujących — najdłuższa fraza.</p>
      {list.error && <p className="alert" role="alert">{list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać skojarzeń'}</p>}
      <ul className="settings-list" aria-label="Skojarzenia miejsc">
        {rules.map((r) =>
          editing === r.id ? (
            <li key={r.id}>
              <RuleForm base={r} onDone={() => setEditing(null)} />
            </li>
          ) : (
            <li key={r.id} className="settings-row">
              <div className="settings-row-main">
                <strong>{r.phrase}</strong>
                <span className="muted">{placeLabel(r.place)}</span>
              </div>
              {confirm?.id === r.id ? (
                <span className="settings-confirm" role="group" aria-label={`Usunąć skojarzenie ${r.phrase}?`}>
                  <span>Usunąć?</span>
                  <button className="btn small danger" type="button" disabled={remove.isPending} onClick={() => remove.mutate(r)}>
                    Usuń
                  </button>
                  <button className="btn small ghost" type="button" onClick={() => setConfirm(null)}>
                    Nie
                  </button>
                </span>
              ) : (
                canEdit && (
                  <RowMenu
                    label={`Działania: ${r.phrase}`}
                    actions={[
                      { label: 'Edytuj', onSelect: () => leaveGuarded(() => setEditing(r.id)) },
                      { label: 'Usuń', danger: true, onSelect: () => setConfirm(r) },
                    ]}
                  />
                )
              )}
            </li>
          ),
        )}
      </ul>
      {list.data && rules.length === 0 && <p className="muted">Brak skojarzeń. Dodaj je tutaj albo kliknij „Zapamiętaj” po utworzeniu wydarzenia.</p>}
      <Problem error={remove.error} conflict="Ktoś już zmienił to skojarzenie. Zamknij i otwórz listę jeszcze raz." />
      {canEdit && editing === 'new' && <RuleForm base={null} onDone={() => setEditing(null)} />}
      {canEdit && editing !== 'new' && (
        <button className="btn small" type="button" onClick={() => leaveGuarded(() => setEditing('new'))}>
          + Dodaj skojarzenie
        </button>
      )}
    </section>
  );
}
